import React, { useState, useEffect, useContext } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary, Alert, AlertTitle,
  Avatar,
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText, Snackbar,
  Typography,
} from '@mui/material';
import { Check } from '@mui/icons-material';
import {useLocation, useNavigate} from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';

import { useBodyClass, useLangParam } from '../hooks';
import { ProgressContext } from '../context/Progress';
import { ApiContext } from '../context/Api';
import HomeModel from '../models/HomeModel';
import Layout from './Layout';
import Loader from '../components/Loader';
import NetworkError from '../components/NetworkError';
import AddToHomescreenPrompt from '../components/AddToHomescreenPrompt';
import '../scss/App.scss';

function Home() {
  useLangParam();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [currentLang] = useCookies(['language']);
  const [show, setShow] = useState(false);
  const { completedLessons } = useContext(ProgressContext);
  const { apiService } = useContext(ApiContext);
  const [expanded, setExpanded] = React.useState('');

  useBodyClass('home');

  const {
    data,
    isLoading,
    isError,
    refetch,
  } = HomeModel();

  const handleExpand = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  useEffect(() => {
    const values = queryString.parse(location.search);
    values.msgsent && setShow(true);
  }, [currentLang, location.search]);

  function lessonIcon(id, slug) {
    if (completedLessons.includes(slug)) {
      return <span className="lesson-icon completed"><Check /></span>;
    } else {
      return <span className="lesson-icon">{id}</span>;
    }
  }

  function progressPercent(course) {
    const courseLessonsCompleted = course.lessons.filter(lesson => {
      return completedLessons.includes(lesson.slug);
    });

    return Math.round((courseLessonsCompleted.length / course.lessons.length) * 100);
  }

  function introIcon(course) {
    const courseLessonsCompleted = course.lessons.filter(lesson => {
      return completedLessons.includes(lesson.slug);
    });

    return courseLessonsCompleted.length ? (
      <span className="lesson-icon completed"><Check /></span>
    ) : (
      <span className="lesson-icon">i</span>
    );
  }

  function iconUrl(url) {
    if (!url) {
      return '';
    }
    // If icon is an absolute URL, just use it
    if (url.indexOf('http') > -1) {
      return url;
    }
    // If icon is relative, use the apiService.cmsUrl since dev is on two different hosts
    // Trim leading/trailing slashes (/) to ensure only one
    return apiService.cmsUrl.replace(/^\/+|\/+$/g, '') + '/' + url.replace(/^\/+|\/+$/g, '');
  }

  if (isError) {
    return (
      <Layout>
        <NetworkError onRefresh={refetch} />
      </Layout>
    );
  }
  if (isLoading) {
    return (
      <Layout>
        <Loader variant="course-list" />
      </Layout>
    )
  }
  return (
    <Layout>
      {data && data.map(course => (
        <Accordion key={course.id} expanded={expanded === course.id} onChange={handleExpand(course.id)}>
          <AccordionSummary
            aria-controls="panel1-content"
            id="panel1-header"
            sx={{ alignItems: 'center' }}
          >
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={iconUrl(course.icon.url)}
                sx={{
                  backgroundColor: 'grey.300',
                  width: 40,
                  height: 40,
                }}
              />
              <CircularProgress
                variant="determinate"
                value={progressPercent(course)}
                thickness={2}
                size={44}
                sx={{
                  position: 'absolute',
                  top: -2,
                  left: -2,
                  zIndex: 1,
                }}
              />
            </Box>
            <Typography component="span" sx={{ fontWeight: '300' }}>{course.name}</Typography>
          </AccordionSummary>
          <AccordionDetails className="accordion-details-lesson-list">
            <List>
              <ListItemButton onClick={() => navigate(`${course.slug}`)}>
                <ListItemIcon>{introIcon(course)}</ListItemIcon>
                <ListItemText>{t('lesson.introduction')}</ListItemText>
              </ListItemButton>
              {course.lessons.map(lesson => (
                <ListItemButton
                  key={lesson.id}
                  onClick={() => navigate(`${course.slug}/${lesson.id}-${lesson.slug}`)}
                >
                  <ListItemIcon>{lessonIcon(lesson.sortOrder, lesson.slug)}</ListItemIcon>
                  <ListItemText
                    primary={lesson.name}
                    secondary={lesson.subtitle}
                  />
                </ListItemButton>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}

      <Button
        onClick={() => navigate('contact')}
        variant="contained"
        color="primary"
        size="large"
        sx={{
          fontSize: '1.25rem',
          borderRadius: 0,
          width: '100%',
        }}
      >
        {t('core.contact')}
      </Button>
      <Snackbar
        open={show}
        autoHideDuration={3000}
        onClose={() => setShow(false)}
      >
        <Alert
          onClose={() => setShow(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          <AlertTitle>{t('core.contact')}</AlertTitle>
          {t('contact.success')}
        </Alert>
      </Snackbar>
      <AddToHomescreenPrompt />
    </Layout>
  );
}

export default Home;
